import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Zap, Clock, Star, Trophy, Rocket, Flame, Play } from "lucide-react";
import { Pill } from "./Neon";
import { gbp, timeParts } from "../lib/format";
import { useSiteConfig } from "../context/SiteConfigContext";

const pad = (n) => String(n).padStart(2, "0");
const num = (n) => Number(n || 0).toLocaleString("en-GB");

export const CompetitionCard = ({ comp }) => {
  const navigate = useNavigate();
  const { showTicketCounts } = useSiteConfig();
  // Tick once a second so the countdown stays live.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const sold = comp.tickets_sold || 0;
  const total = comp.total_tickets || 0;
  const pct = total ? Math.min(100, Math.round((sold / total) * 100)) : 0;
  const t = timeParts(comp.ends_at);
  const endingSoon = !t.done && t.d < 2;
  const isLaunch = !!(comp.launch_draw || comp.is_launch_competition);
  // Total instant-win value still to be won. Prefer the backend's dedicated field, else
  // sum value × remaining across prizes. Guide: show the total only, never per-prize counts.
  const iwTotal = Number(comp.instant_wins_value) > 0
    ? Number(comp.instant_wins_value)
    : (comp.instant_win_prizes || []).reduce((s, p) => s + (Number(p.value) || 0) * (Number(p.remaining) || 0), 0);
  const showInstantWins = !!comp.instant_wins_enabled && (iwTotal > 0 || (comp.instant_wins_remaining || 0) > 0);
  // Ticket counts are hidden by default — revealed when EITHER the global config flag OR
  // the per-competition flag/metadata tag is on.
  const showTickets = showTicketCounts === true
    || comp.show_ticket_count === true
    || (Array.isArray(comp.metadata_tags) && comp.metadata_tags.includes("show_ticket_count"));
  const sellingFast = pct >= 70;
  const countdown = t.done ? "Ended" : `${t.d}d ${pad(t.h)}:${pad(t.m)}:${pad(t.s)}`;
  const go = () => navigate(`/competitions/${comp.id}`);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={go}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); go(); } }}
      data-testid={`competition-card-${comp.id}`}
      className={`group h-full cursor-pointer transition-transform duration-200 hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--neon-cyan)] rounded-2xl ${isLaunch ? "countdown-gold" : "neon-flow"}`}
    >
      <div className="neon-inner h-full flex flex-col overflow-hidden p-0">
        {/* Banner — full 16:9, never cropped awkwardly */}
        <div className="relative aspect-[16/9] overflow-hidden bg-black">
          {comp.hero_video
            ? <video src={comp.hero_video} poster={comp.hero_image || undefined} autoPlay muted loop playsInline preload="metadata" className="h-full w-full object-cover" data-testid={`card-hero-video-${comp.id}`} />
            : comp.hero_image
              ? <img src={comp.hero_image} alt={comp.title} loading="lazy" decoding="async" className="h-full w-full object-cover" />
              : <div className="h-full w-full bg-[var(--surface-2)]" />}
          {/* Top-left badge(s) */}
          <div className="absolute top-3 left-3 flex flex-wrap gap-2">
            {comp.hero_video && (
              <span data-testid={`animated-badge-${comp.id}`} className="inline-flex items-center gap-1 rounded-full bg-[var(--neon-gold)] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-black shadow-[0_0_12px_rgba(255,184,0,.5)]">
                <Play className="h-3 w-3" /> Animated
              </span>
            )}
            {isLaunch ? (
              <span data-testid={`launch-badge-${comp.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-[var(--neon-gold)] px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-black shadow-[0_0_16px_rgba(255,184,0,.55)]">
                <Rocket className="h-3.5 w-3.5" /> Launch Special
              </span>
            ) : (
              <>
                {comp.featured && <Pill color="pink"><Star className="h-3 w-3" /> Featured</Pill>}
                {comp.instant_wins_enabled && <Pill color="cyan"><Zap className="h-3 w-3" /> Instant Wins</Pill>}
                {comp.subscriber_eligible && <Pill color="green">Members</Pill>}
                {endingSoon && <Pill color="gold"><Clock className="h-3 w-3" /> Ending Soon</Pill>}
              </>
            )}
          </div>
          {/* Top-right ticket price pill */}
          <div className="absolute top-3 right-3">
            <span data-testid={`card-price-${comp.id}`} className="inline-flex items-center rounded-full bg-[var(--neon-pink)] px-3.5 py-1 text-sm font-bold text-white shadow-[0_0_16px_rgba(255,45,149,.5)]">
              {gbp(comp.ticket_price)}
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="flex flex-1 flex-col gap-3 p-5">
          <h3 className="font-display text-2xl text-white leading-tight line-clamp-1">{comp.title}</h3>
          <p className="flex items-center gap-2 text-sm text-white/90">
            <Trophy className="h-4 w-4 shrink-0 text-[var(--neon-gold)]" />
            <span className="line-clamp-1">{comp.prize_title || `${gbp(comp.prize_value)} Prize`}</span>
          </p>
          {showInstantWins && (
            <p className="flex items-center gap-2 text-sm text-[var(--neon-cyan)]">
              <Zap className="h-4 w-4 shrink-0" />
              <span className="line-clamp-1">{iwTotal > 0 ? `${gbp(iwTotal)} instant wins to be won` : `${num(comp.instant_wins_remaining)} instant wins to be won`}</span>
            </p>
          )}
          {!t.done && (
            <p className="flex items-center gap-2 font-display text-lg tracking-wide text-[var(--neon-cyan)]">
              <Clock className="h-4 w-4" /> {countdown}
            </p>
          )}

          {/* Progress + sold info */}
          <div className="mt-auto pt-1">
            <div className="h-2.5 w-full rounded-full bg-white/10 overflow-hidden">
              <div className="h-full rrc-progress" style={{ width: `${pct}%`, ...(sellingFast ? { background: "var(--neon-pink)", boxShadow: "0 0 10px rgba(255,45,149,.6)" } : {}) }} />
            </div>
            <div className="mt-2 flex items-center justify-between text-sm">
              {sellingFast ? (
                <span className="flex items-center gap-1 font-display uppercase tracking-wide text-[var(--neon-pink)]" data-testid={`pct-sold-${comp.id}`}>
                  <Flame className="h-4 w-4" /> {pct}% Sold — selling fast!
                </span>
              ) : (
                <span className="font-display uppercase tracking-wide text-[var(--neon-green)]" data-testid={`pct-sold-${comp.id}`}>{pct}% Sold</span>
              )}
              {total > 0 && (total - sold) <= total * 0.1 && (
                <span className="font-display uppercase tracking-wide text-[var(--warning,#FFEA00)]" data-testid={`tickets-hint-${comp.id}`}>Last 10%!</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
